// HubAppSwitcher — navbar dropdown listing the calling user's app
// memberships. Gated by REACT_APP_HUB_NAV_ENABLED. Admins do NOT see it
// (they navigate via /admin). Non-authenticated users do NOT see it.
//
// Click on an active app → navigate to its app route.
// Click on a pending_onboarding app → open OnboardingWizard.
// "Explore" → /apps (public marketing grid).
// "Manage" → /app/settings/apps (F4 page).

import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Grid3x3, CheckCircle2, AlertTriangle, XCircle, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { useHubI18n } from "@/i18n/hubI18n";
import { useAppMemberships } from "@/components/hub/useAppMemberships";
import OnboardingWizard from "@/components/hub/OnboardingWizard";

const APP_ROUTES = {
  team_core: "/app",
  racket_lab: "/app/racket-lab",
  marketplace: "/app/marketplace",
  wlp: "/app/wlp",
  team_tennis: "/apps/team_tennis",
  padel: "/apps/padel",
  pickleball: "/apps/pickleball",
};

const HubAppSwitcher = () => {
  const { lang } = useLang();
  const { user } = useAuth();
  const i18n = useHubI18n(lang);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [wizardApp, setWizardApp] = useState(null);
  const wrapperRef = useRef(null);
  // Landing-bugfix-01 (Feb 2026): the switcher mounts in the Navbar
  // for every route, including the public landing and /register. The
  // `useAppMemberships()` hook MUST NOT fetch `/api/app-memberships/me`
  // when the visitor is anonymous — that call returns 401 and was
  // surfaced as an "Uncaught runtime error" overlay on the public
  // pages. We pass an explicit `shouldFetch` to the hook so it
  // short-circuits before any axios call when the gates aren't met.
  const flagOn = process.env.REACT_APP_HUB_NAV_ENABLED === "true";
  const isAdminOrStaff = !!user && (user.role === "admin" || user.role === "staff");
  const shouldFetch = flagOn && !!user && !isAdminOrStaff;
  const { memberships, isLoading, refresh } = useAppMemberships(shouldFetch);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Gates: feature flag + auth + non-admin
  if (process.env.REACT_APP_HUB_NAV_ENABLED !== "true") return null;
  if (!user) return null;
  if (user.role === "admin" || user.role === "staff") return null;

  const isPlayer = !isAdminOrStaff && !!user && user.profile_key === "player";
  // RACKET-LAB-PLAYER-EQUIPMENT-UX-3 (Feb 2026) — for Player profile we
  // ALWAYS show a single synthetic Racket Lab entry pointing to the
  // authenticated player dashboard (`/app#player-racket-lab`). Real
  // memberships (including any legacy `marketplace` row that may still
  // live in the DB) are intentionally ignored for this surface so the
  // VIP demo can never leak `Marketplace`, `My Racket Lab` or a public
  // landing into the player experience.
  const SYSTEM_APPS = new Set(["team_core"]);
  const PLAYER_HIDDEN_APPS = new Set(["marketplace"]);
  let visible;
  if (isPlayer) {
    visible = [{
      id: "synthetic-racket-lab-player",
      app_key: "racket_lab",
      status: "active",
      is_synthetic_player_entry: true,
    }];
  } else {
    visible = (memberships || []).filter(
      (m) => m.status === "active" || m.status === "pending_onboarding" || m.status === "suspended"
    ).filter((m) => {
      if (SYSTEM_APPS.has(m.app_key)) return false;
      if (PLAYER_HIDDEN_APPS.has(m.app_key)) return false;
      return true;
    });
  }

  const statusIcon = (status) => {
    if (status === "active") return <CheckCircle2 className="w-4 h-4 text-emerald-600" data-testid={`hub-status-active`} />;
    if (status === "pending_onboarding") return <AlertTriangle className="w-4 h-4 text-amber-600" data-testid={`hub-status-pending`} />;
    if (status === "suspended") return <XCircle className="w-4 h-4 text-red-600" data-testid={`hub-status-suspended`} />;
    return null;
  };
  const statusLabel = (status) => {
    if (status === "active") return i18n.statusActive;
    if (status === "pending_onboarding") return i18n.statusPending;
    if (status === "suspended") return i18n.statusSuspended;
    return "";
  };

  const onAppClick = (m) => {
    setOpen(false);
    if (m.is_synthetic_player_entry) {
      // RACKET-LAB-PLAYER-EQUIPMENT-UX-3 — anchor jumps to the Racket
      // Lab section inside the authenticated player dashboard.
      navigate("/app#player-racket-lab");
      return;
    }
    if (m.status === "pending_onboarding") {
      setWizardApp(m.app_key);
      return;
    }
    if (m.status === "suspended") {
      navigate("/app/settings/apps");
      return;
    }
    const route = APP_ROUTES[m.app_key];
    if (route) navigate(route);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="hub-app-switcher-wrapper">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition"
        data-testid="hub-app-switcher-button"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Grid3x3 className="w-4 h-4 text-emerald-700" />
        <span>{i18n.switcherTrigger}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 rounded-xl border border-slate-200 bg-white shadow-lg z-50"
          data-testid="hub-app-switcher-popover"
          role="menu"
        >
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {i18n.switcherTitle}
            </div>
          </div>
          <div className="max-h-72 overflow-auto py-1">
            {isLoading ? (
              <div className="px-4 py-3 text-sm text-slate-500" data-testid="hub-switcher-loading">{i18n.loading}</div>
            ) : visible.length === 0 ? (
              <div className="px-4 py-4 text-sm text-slate-500" data-testid="hub-switcher-empty">
                {i18n.switcherEmpty}
              </div>
            ) : (
              visible.map((m) => (
                <button
                  type="button"
                  key={m.app_key}
                  onClick={() => onAppClick(m)}
                  className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-slate-50 text-start transition"
                  data-testid={`hub-app-item-${m.app_key}`}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-slate-900 truncate">
                      {i18n.appLabels[m.app_key] || m.app_key}
                    </span>
                    <span className="text-[11px] text-slate-500 flex items-center gap-1">
                      {statusIcon(m.status)} {statusLabel(m.status)}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              ))
            )}
          </div>
          <div className="border-t border-slate-100 py-1">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/apps"); }}
              className="w-full text-start px-4 py-2 text-xs text-slate-600 hover:bg-slate-50 transition"
              data-testid="hub-switcher-explore"
            >
              + {i18n.exploreMore}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/app/settings/apps"); }}
              className="w-full text-start px-4 py-2 text-xs text-emerald-700 hover:bg-emerald-50 font-medium transition"
              data-testid="hub-switcher-manage"
            >
              ⚙ {i18n.manageMyApps}
            </button>
          </div>
        </div>
      )}

      {wizardApp && (
        <OnboardingWizard
          appKey={wizardApp}
          onClose={() => { setWizardApp(null); refresh(); }}
        />
      )}
    </div>
  );
};

export default HubAppSwitcher;
